/* eslint-disable import/no-cycle */
/* eslint-disable no-param-reassign */ // we can "afford" that, because under createSlice there is Immer which does the magic
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { getSchemaByCategory } from '@monestry-dev/schema';
import { apiGetAuth, apiPostAuth, apiPutAuth, apiDeleteAuth } from '../../amplify/restClient';
import { getAuthHeaders } from '../../amplify/authUtilities';
import { queueQuotesRequest } from './sharedThunks/quotesRequestPlanner';
import { buildScopesFromTransactions, normalizeDates } from './sharedThunks/quoteScopes';
import { setMessage } from '../actions/message';
import { globalTransactionViewPerCategoryWithIsolated } from './globalSelectors/overarching';
import { globalAccountsView, globalBaselineView } from './globalSelectors';
import { SET_APP_SIGNAL, CLEAR_APP_SIGNAL } from '../actions/types';
import i18n from '../../i18n';
import { getProjects } from '../actions/projects';
import { settingsStocksAndCrypto as defaultSettings } from '../../elements/config';

const utc = require('dayjs/plugin/utc');

dayjs.extend(utc);

const DATA_BOOTSTRAP_SESSION_KEY_PREFIX = 'dataBootstrapDone:';
const inMemoryDataBootstrapFlags = {};

function getSessionStorage() {
  if (typeof window !== 'undefined' && window.sessionStorage) return window.sessionStorage;
  if (typeof sessionStorage !== 'undefined') return sessionStorage;
  return null;
}

function getDataBootstrapSessionKey(category) {
  return `${DATA_BOOTSTRAP_SESSION_KEY_PREFIX}${category}`;
}

function hasDataBootstrapRun(category) {
  const sessionKey = getDataBootstrapSessionKey(category);
  const storage = getSessionStorage();
  if (storage) return storage.getItem(sessionKey) === '1';
  return inMemoryDataBootstrapFlags[sessionKey] === '1';
}

function markDataBootstrapDone(category) {
  const sessionKey = getDataBootstrapSessionKey(category);
  const storage = getSessionStorage();
  if (storage) storage.setItem(sessionKey, '1');
  inMemoryDataBootstrapFlags[sessionKey] = '1';
}

function clearDataBootstrapDone(category) {
  const sessionKey = getDataBootstrapSessionKey(category);
  const storage = getSessionStorage();
  if (storage) storage.removeItem(sessionKey);
  delete inMemoryDataBootstrapFlags[sessionKey];
}

// status 'inactive' is relevant for postFile being exposed to the endpoint
// Dashboard has to wait until we know that all getData have run (they end either in success or in error)
const initialState = {
  deposits: {
    status: 'loading',
    accounts: [],
    transactions: [],
    simulatedTransactions: [],
    batchIds: null,
  },
  stocks: {
    status: 'loading',
    accounts: [],
    transactions: [],
    simulatedTransactions: [],
    settings: {},
    batchIds: null,
  },
  realEstate: {
    status: 'loading',
    accounts: [],
    transactions: [],
    simulatedTransactions: [],
  },
  loans: {
    status: 'loading',
    accounts: [],
    transactions: [],
    simulatedTransactions: [],
    batchIds: null,
  },
  pension: {
    status: 'loading',
    accounts: [],
    transactions: [],
    simulatedTransactions: [],
    batchIds: null,
  },
  objectsOfValue: {
    status: 'loading',
    accounts: [],
    transactions: [],
    simulatedTransactions: [],
    batchIds: null,
  },
  metals: {
    status: 'loading',
    accounts: [],
    transactions: [],
    simulatedTransactions: [],
    batchIds: null,
  },
  unlistedShares: {
    status: 'loading',
    accounts: [],
    transactions: [],
    simulatedTransactions: [],
    batchIds: null,
  },
  crypto: {
    status: 'loading',
    accounts: [],
    transactions: [],
    simulatedTransactions: [],
    batchIds: null,
  },
};

function parseIf(exp) {
  return typeof exp === 'string' ? JSON.parse(exp) : exp;
}

async function prepCallToApi(body) {
  const headers = await getAuthHeaders({ 'Content-Type': 'application/json' });
  const myInit = {
    headers,
  };
  if (body !== undefined) {
    myInit.body = body;
  }
  return myInit;
}

function getApiErrorStatus(error) {
  return error?.response?.status || error?.statusCode || error?.$metadata?.httpStatusCode || null;
}

function getApiErrorDetails(error) {
  return error?.response?.data || error?.message || error;
}

// runs on transactions received from APIs, just before they are added to the store
function getTransactionTransformationsCallback(category) {
  return function addGenericTransactionsFieldsCallback(item) {
    // TODO this needs to be also reflected in the backend database at some point; this is a workaround for PRD-1410
    // i.e. the currency conversion should probably me moved out of the category backend and into quotes for arch consistency (for current fx value)
    if (category === 'deposits') {
      return {
        ...item,
        originalUnitPrice: 1,
        unitPrice: 1,
        tags: parseIf(item.tags),
        // new universal selectors
        category,
        assetId: item.currency, // logic: this is the currency in which the money is held
        // at the moment when the transaction gets created, the amount is already in account currency
        // we need to show user the original _value_ i.e. quantity * uptc (we never show quantity alone)
        quantity: item.accountCurrencyAmount,
        accountCurrency: item.currency,
        upac: 1,
        upbc: item.accountCurrencyAmount ? item.amount / item.accountCurrencyAmount : 1, // if the accountCurrencyAmount is 0, then upbc does not matter
        uptc: item.fxAmount && item.accountCurrencyAmount ? item.fxAmount / item.accountCurrencyAmount : 1, // check if there is accountCurrencyAmount, because it can be 0
        transactionCurrency: item.fxCurrency || item.currency,
        transactionType: item.accountCurrencyAmount >= 0 ? 'purchase' : 'sale',
      };
    }
    if (category === 'stocks' || category === 'crypto') {
      const { linkedTransactions } = item;
      const parsedLT = parseIf(linkedTransactions) || [];
      let newLT;
      if (parsedLT.length > 0) {
        // TODO move to backend remaps rebasedAmount and rebasedPrice to quantity and upbc / uptc
        newLT = parsedLT.map((lt) => {
          if (lt.tags.type === 'purchase') {
            return { ...lt, tags: { ...lt.tags, quantity: lt.tags?.rebasedAmount, upbc: lt.tags?.rebasedPrice } };
          }
          return lt;
        });
      }
      return {
        ...item,
        originalUnitPrice: item.rebasedOriginalPrice, // should be always populated by backend
        unitPrice: item.rebasedPrice, // should be always populated by backend
        amount: item.amount || item.rebasedAmount,
        tags: parseIf(item.tags),
        linkedTransactions: newLT,
        // new universal selectors
        category,
        assetId: item.figi,
        quantity: item.rebasedAmount, // should be always populated by backend
        upac: item.rebasedOriginalPrice,
        accountCurrency: item.transactionCurrency, // see wiki
        upbc: item.rebasedPrice,
        uptc: item.rebasedOriginalPrice,
        transactionCurrency: item.transactionCurrency,
      };
    }
    if (category === 'realEstate') {
      return {
        ...item,
        amount: item.amount || item.rebasedAmount,
        originalUnitPrice: item.rebasedOriginalPrice || item.originalPrice || 1,
        unitPrice: item.rebasedPrice || item.price || 1,
        tags: parseIf(item.tags),
        // new universal selectors
        category,
        assetId: item.accountId, // changed by PRD-164, as quotes currently only support providerAssetId
        quantity: Number(item.amount),
        // quantitySold, quantityOpen added in realEstateTransactions
        upac: item.originalPrice,
        accountCurrency: item.currency,
        upbc: item.price,
        uptc: item.originalPrice,
        transactionCurrency: item.currency,
        transactionType: Number(item.amount) >= 0 ? 'purchase' : 'sale',
      };
    }
    if (category === 'loans') {
      return {
        ...item,
        category,
        quantity: Number(item.quantity),
        quantityInterest: Number(item.quantityInterest),
        upac: Number(item.upac),
        upbc: Number(item.upbc),
        uptc: Number(item.uptc),
        assetCurrency: item.accountCurrency,
        assetId: item.accountCurrency,
        // adding transactionType in loanTransactions, as we need account object to decide that
      };
    }
    if (category === 'pension') {
      return {
        ...item,
        category,
        quantity: Number(item.quantity),
        quantityInterest: Number(item.quantityInterest),
        upac: Number(item.upac),
        upbc: Number(item.upbc),
        uptc: Number(item.uptc),
        assetCurrency: item.accountCurrency,
        assetId: item.accountId,
      };
    }
    if (category === 'objectsOfValue') {
      return {
        ...item,
        category,
        quantity: Number(item.quantity),
        upac: Number(item.upac),
        upbc: Number(item.upbc),
        uptc: Number(item.uptc),
        assetCurrency: item.accountCurrency,
        assetId: item.accountId,
      };
    }
    if (category === 'metals') {
      return {
        ...item,
        category,
        quantity: Number(item.quantity),
        upac: Number(item.upac),
        upbc: Number(item.upbc),
        uptc: Number(item.uptc),
        assetCurrency: item.accountCurrency,
        // this field is calculated here; it is used in getQuotes API to calculate the current value of the asset based on pure metal market prices and its metal contents
        providerAssetId: { assetMetal: item.assetMetal, assetMetalWeight: item.assetWeight * (item.assetPurity / 1000) },
      };
    }
    if (category === 'unlistedShares') {
      return {
        ...item,
        category,
        quantity: Number(item.quantity),
        upac: Number(item.upac),
        upbc: Number(item.upbc),
        uptc: Number(item.uptc),
        assetCurrency: item.accountCurrency,
        assetId: item.accountId,
      };
    }
    return item;
  };
}

function getAccountTransformationCallback(category, state) {
  return function accountTransformationsCallback(a) {
    if (category === 'loans') {
      return {
        ...a,
        category,
        loanAmount: Number(a.loanAmount),
        tags: parseIf(a.tags),
        period: parseIf(a.period),
        percentRepaidAnnually: parseIf(a.percentRepaidAnnually),
        interestRate: parseIf(a.interestRate),
        connectedDepositAccounts: parseIf(a.connectedDepositAccounts),
      };
    }
    if (category === 'pension') {
      const parsedCustomDiscountRate = a.customDiscountRate === '' || a.customDiscountRate === undefined || a.customDiscountRate === null
        ? null
        : Number(a.customDiscountRate);
      const hasExplicitDiscountMode = a.discountRateIsInflation === true || a.discountRateIsInflation === false;
      const defaultDiscountRateIsInflation = hasExplicitDiscountMode
        ? a.discountRateIsInflation
        : parsedCustomDiscountRate === null;

      return {
        ...a,
        category,
        initialQuotes: parseIf(a.initialQuotes),
        contributionsAmounts: parseIf(a.contributionsAmounts),
        tags: parseIf(a.tags),
        connectedDepositAccounts: parseIf(a.connectedDepositAccounts),
        customDiscountRate: parsedCustomDiscountRate,
        // Legacy accounts may have neither discount mode nor custom rate set.
        // Defaulting to inflation keeps PV calculations non-zero and matches wizard defaults.
        discountRateIsInflation: defaultDiscountRateIsInflation,
      };
    }
    return {
      ...a,
      category,
      tags: parseIf(a.tags),
      ...(a.valuationParameters && { valuationParameters: parseIf(a.valuationParameters) }),
      importFileMappings: parseIf(a.importFileMappings),
      ...(category !== 'deposits' && { connectedDepositAccounts: parseIf(a.connectedDepositAccounts) }), // changed by PRD-164, as quotes currently only support providerAssetId
    };
  };
}

async function handleExpiredCredentialsError({ errorAccountObject, dispatch, getState, category }) {
  console.log('handleExpiredCredentialsError for account', errorAccountObject);

  const state = getState();
  const account = globalAccountsView(state).find((a) => a.id === errorAccountObject.accountId);
  console.log('handleExpiredCredentialsError: account', account);
  if (!account) return;

  const providerInstitutionId = account.tags.provider?.finapi?.providerInstitutionId || account.tags.provider?.gocardless?.providerInstitutionId;
  const webFormUrl = errorAccountObject.message?.webForm?.url;

  const redirectUrl = `${window.location.origin}/addAccountCallback`;
  const abortUrl = `${window.location.origin}/${i18n.language}/app/dashboard`;
  // show dialog to user
  window.dispatchEvent(
    new CustomEvent('setAlert', {
      detail: {
        id: 'expiredCredentialsError',
        args: { accountName: account.name },
        caller: 'getData',
        callbackOk: async () => {
          console.log('handleExpiredCredentialsError: starting call to API');
          const requestPayload = await prepCallToApi({
            country: account.countryCode,
            userLanguage: i18n.language,
            institutionId: providerInstitutionId,
            redirectUrl,
            abortUrl,
          });
          try {
            // finApi already provides webFormUrl in error response, so we can use that
            if (webFormUrl) {
              // create localStorage object (to be reopened after coming back from webform in AddAccountCallbackReceiver / Dashboard)
              // no need to store any additional parameters for finApi, because existing providerAccountIds remain unchanged
              localStorage.setItem('renewAccountAccess', JSON.stringify({ accountId: errorAccountObject.accountId, category }));
              // navigate to the web form
              window.location.href = `${webFormUrl}?redirectUrl=${encodeURIComponent(redirectUrl)}&errorRedirectUrl=${encodeURIComponent(abortUrl)}&abortRedirectUrl=${encodeURIComponent(abortUrl)}`;
            } else {
              // returns { url, processId } or { url: null, processId: null, bankConnectionId, accounts } in the case handled just below
              const response = await apiPostAuth('myAPI', 'deposits/webform', requestPayload);
              console.log('handleExpiredCredentialsError: response from API', response);

              // create localStorage object (to be reopened after coming back from webform in AddAccountCallbackReceiver / Dashboard);
              // GC creates a new End-user Agreement ID + requsition ID with new providerAccountIds every time the EUA expires;
              // we need to re-map our accountIds from old to new providerAccountIds by getting the new ones and mapping their IBANs to our IBANs in AddAccountCallbackReceiver;
              // (we can retrieve the new providerAccountIds from the getAccountDetails service by processId (which is GC's requsition ID))
              localStorage.setItem('renewAccountAccess', JSON.stringify({ accountId: errorAccountObject.accountId, category, reconcileAccounts: true, processId: response.processId }));

              window.location.href = response.url;
            }
          } catch (err) {
            console.error('reducers > data > handleExpiredCredentialsError > error in API.post', err);
          }
        },
      },
    }),
  );
}

export const updateStoreDataWrapper = createAsyncThunk('data/updateStoreDataWrapper', async (payload, { dispatch }) => {
  // updateDepositAccountIds: Array<deposit accountId>
  // if delete asset account or delete asset transaction had to remove a tag from a linked deposit transaction, we need to refresh the deposit account with that transaction
  // so that the change is visible in the frontend
  const { updateDepositAccountIds } = payload;
  if ((updateDepositAccountIds || []).length > 0) {
    updateDepositAccountIds.forEach((updateDepositAccountId) => {
      // eslint-disable-next-line no-use-before-define
      dispatch(getDataByAccount({ category: 'deposits', accountId: updateDepositAccountId }));
    });
  }

  // once done that, update store data through the common reducer as usual
  dispatch({ type: 'data/updateStoreData', payload });
});

// this retrieves stocks and cypto settings, as they share a backend, and splits them into the correct categories based on the argument
export const getStocksSettings = createAsyncThunk('data/getStocksSettings', async (category, { dispatch }) => {
  const payload = await prepCallToApi(null);
  const response = await apiGetAuth('myAPI', 'stocks/settings', payload);
  // parsing: when set automatically via terraform this is an object already, when set manually it is a string
  const responseParsed = parseIf(response);

  const { crypto: responseCryptoSettings, ...responseStocksSettings } = responseParsed;
  const { crypto: defaultCryptoSettings, ...defaultStocksSettings } = defaultSettings;

  if (category === 'crypto') {
    return {
      ...defaultCryptoSettings, // making sure all default fields are available
      ...responseCryptoSettings, // spreading the actual user-defined settings on top of that
    };
  }

  return {
    ...defaultStocksSettings, // making sure all default fields are available
    ...responseStocksSettings, // spreading the actual user-defined settings on top of that
  };

  // state updated in getDataByCategory
});

// data is a property within stock.settings; send other properties otherwise they won't be updated
// this has to handle crypto settings, which are stored in .crypto property of settings with the same structure as stock settings
// this will SPREAD data on top of the existing settings, so only the properties that are sent will be updated
// this can be called with category === null to update both stocks and crypto settings at the same time
export const putStocksSettings = createAsyncThunk('data/putStocksSettings', async ({ data, category }, { dispatch, getState }) => {
  const stocksSettings = getState().data.stocks.settings;
  const cryptoSettings = getState().data.crypto.settings;

  let dataAdjusted;
  if (category === 'crypto') {
    // if this is called from crypto, then spread 'data' over the .crypto property only
    dataAdjusted = { ...stocksSettings, crypto: { ...cryptoSettings, ...data } };
  } else {
    // if this is called from stocks or with category === null
    // Planning provides only the parameters that have changed, therefore need to re-spread both stocks and crypto settings from store
    // and then spread 'data' (new changes) over them, both in stocks and in crypto (the latter can be undefined, that's ok)
    dataAdjusted = { ...stocksSettings, ...data, crypto: { ...cryptoSettings, ...data.crypto } };
  }

  const payload = await prepCallToApi(dataAdjusted);
  const response = await apiPutAuth('myAPI', 'stocks/settings', payload);
  const parsedResponse = parseIf(response);
  // this will return the entire settings object, i.e. { growthRateAdvancedMode, growthRate, ..., crypto: { growthRateAdvancedMode, growthRate, ... }}

  const { crypto, ...responseStocksSettings } = parsedResponse;
  // we got a response for both categories in one object, but depending on what category did we get called with (stocks, crypto, null = both) we will update the store with the correct category
  if (category === 'crypto' || category === null) {
    dispatch(updateStoreDataWrapper({ category: 'crypto', settings: crypto }));
  }
  if (category === 'stocks' || category === null) {
    dispatch(updateStoreDataWrapper({ category: 'stocks', settings: responseStocksSettings }));
  }

  return response;
});

function generatePensionPurchaseTransaction(accounts) {
  const returnArray = [];
  accounts.forEach((account) => {
    returnArray.push({
      date: account.productPurchaseDate,
      assetId: account.id,
      accountCurrency: account.currency,
    });
  });
  return returnArray;
}

function getNamedRequiredDates(state) {
  const { baseline, referenceBaseline, current } = globalBaselineView(state);
  return normalizeDates([baseline, referenceBaseline, current]);
}

// used ONLY by AddAccountCallbackReceiver to update the accounts in the store with the new providerAccountIds
// caution: this DOES NOT update category status, because AddAccountCallbackReceiver will navigate to Dashboard, which has to get data for all 'idle' categories (so deposits must be 'idle')
export const getAccountsByCategory = createAsyncThunk('data/getAccountsByCategory', async (category, { dispatch }) => {
  let data;
  try {
    const payload = await prepCallToApi(null);
    data = await apiGetAuth('myAPI', `${(category === 'crypto' ? 'stocks' : category).toLowerCase()}/data`, payload); // crypto is a virtual category --> backend handled by stocks api

    const transformedData = data;
    transformedData.accounts = transformedData.accounts
      .filter((a) => (category === 'crypto' ? a?.subcategory === 'crypto' : a?.subcategory !== 'crypto')) // if this is crypto, stocks api returns also stock accounts, so we need to filter them out
      .map(getAccountTransformationCallback(category));

    const returnPayload = {
      accounts: transformedData.accounts,
      category,
    }; // returns { accounts: [], category: 'deposits' }
    return returnPayload;
  } catch (err) {
    console.log('error in getAccountsByCategory', getApiErrorDetails(err));
    dispatch(setMessage('dataUpdateError'));
  }
  return true;
});

/**
 * Handles API call to /data, transformation and updates redux. If no call mode is passed, the backend will call provider and return both existing and new data.
 *
 * @param {string} category - category to be retrieved
 * @param {func} dispatch - passed from thunk
 * @param {func} getState - passed from thunk
 * @param {string} accountId (optional) - if not provided, the function will retrieve the entire category
 * @param {enum} callMode - one of 'newProviderData', 'existingData', null
 */

async function callGetDataAndTransform(category, dispatch, getState, accountId = null, callMode = null, options = {}) {
  const { replaceCategoryPartition = false } = options;
  if (!['newProviderData', 'existingData', null].includes(callMode)) throw new Error('callGetDataAndTransform: callMode must be one of newProviderData, existingData, null');

  let data;
  let quotes;
  let settings;
  let fxRates;
  let payload;

  try {
    payload = await prepCallToApi(null);
    // call category/data in category
    data = await apiGetAuth(
      'myAPI',
      `${(category === 'crypto' ? 'stocks' : category).toLowerCase()}/data${accountId ? '/accounts/' : ''}${accountId || ''}${callMode ? `?callMode=${callMode}` : ''}`,
      payload,
    ); // crypto is a virtual category --> backend handled by stocks api
  } catch (err) {
    const errorStatus = getApiErrorStatus(err);
    // handle timeout (most likely caused by long provider data refresh times)
    if (errorStatus === 504) {
      // retry in retryMode, which bypasses the backgroundUpdate check in the backend
      try {
        data = await apiGetAuth(
          'myAPI',
          `${(category === 'crypto' ? 'stocks' : category).toLowerCase()}/data${accountId ? '/accounts/' : ''}${accountId || ''}?retryMode=1${callMode ? `&callMode=${callMode}` : ''}`,
          payload,
        ); // crypto is a virtual category --> backend handled by stocks api
      } catch (err2) {
        // if it throws error again, then we have to handle it as a regular error
        console.error(
          'Redux: data reducer: callGetDataAndTransform: retry of API.get error in category:',
          category,
          accountId ? `for accountId ${accountId}` : '',
          'error message:',
          getApiErrorDetails(err2),
        );
        throw err2;
      }
    } else {
      // handle non-504 errors here
      console.error('Redux: data reducer: callGetDataAndTransform: error while contacting API', getApiErrorDetails(err));
      throw err;
    }
  }

  // transform data

  try {
    const transformedData = data;

    transformedData.accounts = transformedData.accounts
      .filter((a) => (category === 'crypto' ? a?.subcategory === 'crypto' : a?.subcategory !== 'crypto')) // if this is crypto, stocks api returns also stock accounts, so we need to filter them out
      .map(getAccountTransformationCallback(category));
    const accountIds = transformedData.accounts.map((a) => a.id);

    transformedData.transactions = transformedData.transactions
      // if this is crypto / stocks, only keep transactions belonging to crypto /stocks accounts
      // (the api will return mixed transactions and they have to be separated)
      .filter((t) => (['crypto', 'stocks'].includes(category) ? accountIds.includes(t.accountId) : true))
      .map(getTransactionTransformationsCallback(category));
    const requiredDates = getNamedRequiredDates(getState());

    // once we have transactions, we can get quotes (crypto shares Stocks API backend)
    if (category === 'stocks' || category === 'crypto') {
      quotes = queueQuotesRequest(
        {
          scopes: buildScopesFromTransactions({
            transactions: transformedData.transactions.filter((tx) => ['purchase', 'sale'].includes(tx.transactionType)),
            requiredDates,
          }),
        },
        { dispatch, debounceMs: 250, immediate: !!accountId },
      );
      settings = dispatch(getStocksSettings(category));
    } else if (category === 'pension') {
      // for pension we need to consider the automatically generated purchase transaction based on whatever is in the account
      const purchaseTransactions = generatePensionPurchaseTransaction(transformedData.accounts);
      quotes = queueQuotesRequest(
        {
          scopes: buildScopesFromTransactions({
            transactions: transformedData.transactions.concat(purchaseTransactions),
            requiredDates,
          }),
        },
        { dispatch, debounceMs: 250, immediate: !!accountId },
      );
    } else {
      quotes = queueQuotesRequest(
        {
          scopes: buildScopesFromTransactions({
            transactions: transformedData.transactions,
            requiredDates,
          }),
        },
        { dispatch, debounceMs: 250, immediate: !!accountId },
      ); // once we have transactions, we can get quotes
    }
    const promisesResponse = await Promise.all([quotes, settings, fxRates]);
    settings = promisesResponse[1]?.payload;

    const returnPayload = {
      ...transformedData,
      transactions: transformedData.transactions.filter((t) => !t.isSimulated),
      simulatedTransactions: transformedData.transactions.filter((t) => !!t.isSimulated),
      settings,
      category,
      replaceCategoryPartition,
    }; // returns { transactions: [], simulatedTransactions: [], accounts: [], status: 'noerrors', category: 'deposits' }
    if (accountId) returnPayload.accountId = accountId; // if this is called from getDataByAccount, we need to return the accountId as well

    // upload data to the store

    await dispatch(updateStoreDataWrapper(returnPayload));

    // handle special case of provider access expiring -- if there are any objects { accountId, code, message } (code being our own errorCode) in data.errors, it means we need to refresh the access
    const errorAccounts = data.errors;
    if (errorAccounts && errorAccounts.length > 0) {
      // kick off the process for the first account in the list; that will take the user to the webform and back to dashboard
      // dashboard will run getData again and if the error still persists for another bank connection, it will be taken from here again
      handleExpiredCredentialsError({
        errorAccountObject: errorAccounts[0],
        dispatch,
        getState,
        category,
      });
    }
  } catch (err) {
    console.error('Redux: data reducer: callGetDataAndTransform error while transforming data');
    throw err;
  }
}

// handles both getDataByCategory and ByAccount
/**
 * getData - retrieve data from API and transform it to redux structure
 *
 * @param {string} category - category to be retrieved
 * @param {func} dispatch - passed from thunk
 * @param {func} getState - passed from thunk
 * @param {string} accountId (optional) - if not provided, the function will retrieve the entire category
 */
async function getData(category, dispatch, getState, accountId = null) {
  const statusActionName = accountId ? 'data/updateAccountStatus' : 'data/updateCategoryStatus';
  const statusPayload = accountId ? { category, accountId } : { category };
  const isCategoryFetch = !accountId;
  const isCategoryBootstrapCall = isCategoryFetch && !hasDataBootstrapRun(category);
  let bootstrapClaimed = false;

  // get data from API
  // TODO PRD-1712 call both call modes in parallel, update store separately and once both are done, update status to success

  try {
    dispatch({ type: statusActionName, payload: { ...statusPayload, status: 'loading' } });
    if (isCategoryBootstrapCall) {
      // Claim category bootstrap before API starts so concurrent requests don't both run replace mode.
      markDataBootstrapDone(category);
      bootstrapClaimed = true;
    }

    const runParallelGetData = ['deposits', 'stocks', 'loans'].includes(category);

    if (runParallelGetData) {
      const existingData = callGetDataAndTransform(category, dispatch, getState, accountId, 'existingData', {
        replaceCategoryPartition: isCategoryBootstrapCall,
      });
      const newProviderData = callGetDataAndTransform(category, dispatch, getState, accountId, 'newProviderData', {
        replaceCategoryPartition: false,
      });

      const existingDataResult = await existingData.then(
        () => ({ ok: true }),
        (error) => ({ ok: false, error }),
      );
      if (existingDataResult.ok) {
        dispatch({ type: statusActionName, payload: { ...statusPayload, status: 'loadingExternal' } });
      }

      const newProviderDataResult = await newProviderData.then(
        () => ({ ok: true }),
        (error) => ({ ok: false, error }),
      );

      if (!existingDataResult.ok) throw existingDataResult.error;
      if (!newProviderDataResult.ok) throw newProviderDataResult.error;
    } else {
      await callGetDataAndTransform(category, dispatch, getState, accountId, 'existingData', {
        replaceCategoryPartition: isCategoryBootstrapCall,
      });
    }

    dispatch({ type: statusActionName, payload: { ...statusPayload, status: 'success' } });
  } catch (err) {
    if (bootstrapClaimed) {
      // Bootstrap failed, so allow next category fetch in this session to retry authoritative replace.
      clearDataBootstrapDone(category);
    }
    console.log('Redux: data reducer: API.get error in category:', category, accountId ? `for accountId ${accountId}` : '', 'error message:', getApiErrorDetails(err));
    dispatch(setMessage('dataUpdateError'));
    dispatch({ type: statusActionName, payload: { ...statusPayload, status: 'error' } });
  }
}

// replaces all transactions / account within a category
export const getDataByCategory = createAsyncThunk('data/getDataByCategory', async (category, { dispatch, getState }) => {
  await getData(category, dispatch, getState);
});

// UPSERTs transactions and accounts within a category
export const getDataByAccount = createAsyncThunk('data/getDataByAccount', async ({ category, accountId }, { dispatch, getState }) => {
  if (!category || !accountId) return;

  await getData(category, dispatch, getState, accountId); // passing accountId starts it in byAccount mode
});

/**
 * @param {object} data - { data: [ transaction-object ], category: 'deposits', accountId: ... }
 * @param {object} cancelSignal - { aborted: false }
 */
export const postData = createAsyncThunk('data/postData', async ({ data, category, accountId, cancelSignal = { aborted: false } }, { dispatch, getState }) => {
  let returnPayload;
  try {
    // cancelSignal is used by Prep to run unmount cleanup; when the component is re-rendered, the useEffect cleanup sets aborted to true
    // if the request is aborted, the dispatches are not run; if aborting functionality is not required, it can be omitted
    const { aborted } = cancelSignal;

    // prevent two postData for the same account at the same time
    const { status } = getState().data[category].accounts.find((a) => a.id === accountId).status || ''; // FIXME handle status null
    if (status === 'loading') {
      console.log('postData: already loading for', category, 'accountId', accountId);
      return {};
    }

    dispatch({ type: 'data/updateCategoryStatus', payload: { category, status: 'loading' } });

    const payload = await prepCallToApi(data);
    const response = !aborted ? await apiPostAuth('myAPI', `${(category === 'crypto' ? 'stocks' : category).toLowerCase()}/transactions`, payload) : { transactions: [], status: 'noerrors', category };

    const { transactions } = response;
    const transformedTransactions = transactions.map(getTransactionTransformationsCallback(category));

    returnPayload = {
      ...response,
      transactions: transformedTransactions.filter((t) => !t.isSimulated),
      simulatedTransactions: transformedTransactions.filter((t) => !!t.isSimulated),
      category,
      accountId,
    }; // returns { transactions: [], simulatedTransactions: [], accounts: [], status: 'noerrors', category: 'deposits', accountId: ... }

    if (
      !aborted
      // eslint-disable-next-line max-len
      && category !== 'deposits'
      && category !== 'loans'
    ) {
      const requiredDates = getNamedRequiredDates(getState());
      const scopeTransactions = transformedTransactions.map(getTransactionTransformationsCallback(category));
      await queueQuotesRequest(
        {
          scopes: buildScopesFromTransactions({
            transactions: scopeTransactions,
            requiredDates,
          }),
        },
        { dispatch, immediate: true },
      ); // once we have transactions, we can get quotes
    }

    if (!aborted) dispatch(updateStoreDataWrapper(returnPayload));

    dispatch({ type: 'data/updateCategoryStatus', payload: { category, status: 'noerrors' } });
  } catch (err) {
    console.log('error in postData', err);
    dispatch(setMessage('dataUpdateError'));
    dispatch({ type: 'data/updateCategoryStatus', payload: { category, status: 'error' } });
    throw err;
  }
  return returnPayload;
});

// dispatches postData request to the correct category service
// payload is an array of transactions with the extra category flag [ { ...transaction-object, category: 'category-name' } ]
// if importFlag is missing in the transaction-object, it will be by default interpreted as POST / PUT
export const postMixedData = createAsyncThunk('data/postMixedData', async ({ payload, cancelSignal = { aborted: false } }, { dispatch, getState }) => {
  // cancelSignal is used by Prep to run unmount cleanup; when the component is re-rendered, the useEffect cleanup sets aborted to true
  // if the request is aborted, the dispatches are not run; if aborting functionality is not required, it can be omitted
  const { aborted } = cancelSignal;

  const updatedPayload = payload.map((item) => {
    if (!item.category) {
      console.error('postData wrapper: transaction is missing a category attribute:', item);
      throw new Error('postMixedData: detected a transaction with no category attribute.');
    }
    if (!item.accountId) {
      // running the view in projectMode, because we need to see the isolated transactions (in case we are peforming a delete op on an isolated project)
      const { accountId } = globalTransactionViewPerCategoryWithIsolated(getState(), item.category).find((t) => t.id === item.id);
      return { ...item, accountId };
    }
    return item;
  });

  // get payload to an object { deposits: { accountId: [transaction-objects], accountId2: [transaction-objects] }, stocks: { ... } }
  const payloadByCategory = updatedPayload.reduce(
    (acc, item) => ({
      ...acc,
      [item.category]: {
        ...acc[item.category],
        [item.accountId]: [...((acc[item.category] || {})[item.accountId] || []), item],
      },
    }),
    {},
  );

  // dispatch data to the correct category service by category, in bulk
  // postData accepts only data per account afair
  const promises = [];
  if (!aborted) {
    Object.keys(payloadByCategory).forEach((item) => {
      Object.keys(payloadByCategory[item]).forEach((accountId) => {
        promises.push(dispatch(postData({ data: payloadByCategory[item][accountId], category: item, accountId })));
      });
    });
    await Promise.all(promises);
  }
});

export const postFileImportData = createAsyncThunk('data/postFileImportData', async ({ data, category, accountId }, { dispatch }) => {
  // only keep transactions from source = 'import' which have a keepFlag of true
  const transactionToPost = data.filter((t) => t.source === 'import' && t.keepFlag === true).map(({ source, keepFlag, isDuplicate, ...t }) => t);

  await dispatch(postData({ data: transactionToPost, category, accountId }));
});

export const postAccount = createAsyncThunk('data/postAccount', async ({ data, category, cancelSignal = { aborted: false } }, { dispatch, getState }) => {
  // cancelSignal is used by Prep to run unmount cleanup; when the component is re-rendered, the useEffect cleanup sets aborted to true
  // if the request is aborted, the dispatches are not run; if aborting functionality is not required, it can be omitted
  const { aborted } = cancelSignal;

  if (!aborted) {
    dispatch({
      // used by ColumnMatcher and by AddNewTile
      type: SET_APP_SIGNAL,
      payload: { callerId: `post${category}Account`, message: 'loading' },
    });
  }

  try {
    const payload = await prepCallToApi({ ...data, importFlag: 'post' });
    // crypto is a virtual category --> backend handled by stocks api, but we need to provide a subcategory for stocks api to know that this is crypto
    if (category === 'crypto') payload.body.subcategory = 'crypto';
    const response = !aborted
      ? await apiPostAuth('myAPI', `${(category === 'crypto' ? 'stocks' : category).toLowerCase()}/accounts`, payload) // crypto is handled by stocks api
      : { accounts: [], status: 'noerrors', category };
    // returns { accounts: [], status: 'noerrors', category: 'deposits' }

    const transformedData = response;
    transformedData.accounts = transformedData.accounts
      .filter((a) => (category === 'crypto' ? a?.subcategory === 'crypto' : true)) // if this is crypto, stocks api returns also stock accounts, so we need to filter them out
      .map(getAccountTransformationCallback(category));
    const account = response.accounts[0];
    const accountId = account.id;

    if (!aborted) dispatch(updateStoreDataWrapper({ ...transformedData, accountId, category }));
    if (!aborted) {
      dispatch({
        // used by ColumnMatcher and by AddNewTile
        type: CLEAR_APP_SIGNAL,
        payload: { callerId: `post${category}Account` },
      });
    }
    if (!aborted) {
      // dispatch(getDataByAccount({ category, accountId })); // FIX 240709 what is the point of doing that here if we post the account? it will be empty
      // WARNING only deposits and stocks (?) may have this implemented in backend
      dispatch(getProjects()); // needed for loans (connectedProjectId dialog)

      if (!aborted && category === 'pension') {
        const requiredDates = normalizeDates(getNamedRequiredDates(getState()).concat([account.productPurchaseDate]));
        const fromDate = account.productPurchaseDate;
        const toDate = Math.max(fromDate, ...requiredDates);
        await queueQuotesRequest(
          {
            scopes: [{
              asset: {
                assetId: accountId,
                providerAssetId: null,
                currency: account.currency,
                category,
              },
              fromDate,
              toDate,
              requiredDates,
              includeManualQuotes: true,
            }],
          },
          { dispatch, immediate: true },
        );
      }
    } // get the newly created account (and its transactions) from the API'})
    if (aborted) console.log('postAccount: request was aborted');
    return transformedData;
  } catch (err) {
    // handle no more slots
    dispatch({
      // used by ColumnMatcher and by AddNewTile
      type: CLEAR_APP_SIGNAL,
      payload: { callerId: `post${category}Account` },
    });
    if (getApiErrorStatus(err) === 507) {
      dispatch(setMessage('noSlots'));
      console.error('Redux: data > postAccount: error while creating the new account (not enough slots available)', err.message);
      throw new Error('noSlotsAvailable: postAccount: error while creating the new account (not enough slots available)'); // 240221 adding this so that Prep sees postAccount failing
      // TODO this should now show up at dispatch in the component and can be handled based on split(':')[0] === 'noSlotsAvailable'
    } else {
      dispatch(setMessage('accountCouldNotBeCreated'));
      console.error('Redux: data > postAccount: error while creating the new account', err.message);
      throw new Error('postAccount: error while creating the new account'); // 240221 adding this so that Prep sees postAccount failing
    }
  }
});

export const putAccount = createAsyncThunk('data/putAccount', async ({ data, category }, { dispatch }) => {
  let normalizedData = data;
  try {
    const schema = getSchemaByCategory(category, 'account');
    if (schema) normalizedData = schema.cast(data);
  } catch (error) {
    console.error(`putAccount: failed to cast ${category} account payload, falling back to raw data`, error);
  }

  const payload = await prepCallToApi({ ...normalizedData, importFlag: 'put' }); // if importFlag is missing, the API assumes POST
  const response = await apiPutAuth('myAPI', `${(category === 'crypto' ? 'stocks' : category).toLowerCase()}/accounts`, payload);

  const transformedData = response;
  transformedData.accounts = transformedData.accounts
    .filter((a) => (category === 'crypto' ? a?.subcategory === 'crypto' : true)) // if this is crypto, stocks api returns also stock accounts, so we need to filter them out
    .map(getAccountTransformationCallback(category));
  const accountId = response.accounts[0].id;

  dispatch(updateStoreDataWrapper({ ...transformedData, accountId, category }));

  if (category === 'deposits') {
    dispatch({
      // used by ColumnMatcher
      type: SET_APP_SIGNAL,
      payload: { callerId: `put${category}Account`, message: 'success' },
    });
  }
});

export const deleteAccount = createAsyncThunk('data/deleteAccount', async ({ accountId, category }, { dispatch }) => {
  const payload = await prepCallToApi({ id: accountId }); // API expects an account object
  const response = await apiDeleteAuth('myAPI', `${(category === 'crypto' ? 'stocks' : category).toLowerCase()}/accounts`, payload);
  dispatch(updateStoreDataWrapper({ ...response, accountId, category }));
  // returns { accounts: [], status: 'noerrors', category: 'deposits' }
  dispatch(setMessage('accountDeletedSuccessfully'));
});

const dataSlice = createSlice({
  name: 'data',
  initialState,
  reducers: {
    hydrate(state, action) {
      // Merge the persisted state into the current state
      return {
        ...state, // Keep the existing state
        ...action.payload, // Overwrite with the values from the persisted state
      };
    },
    updateCategoryStatus(state, action) {
      // expects payload = { category: 'deposits', status: 'loading' }
      state[action.payload.category].status = action.payload.status;
    },
    updateAccountStatus(state, action) {
      // expects payload = { category: 'deposits', accountId: 'account1', status: 'loading' }
      state[action.payload.category].accounts.find((a) => a.id === action.payload.accountId).status = action.payload.status;
    },
    updateStoreData(state, action) {
      const {
        category,
        transactions,
        simulatedTransactions,
        accounts,
        settings,
        replaceCategoryPartition,
      } = action.payload;
      // remove from state all items whose ids are in payload add all those items from payload except those which are to be deleted
      if (replaceCategoryPartition) {
        state[category].transactions = [];
        state[category].simulatedTransactions = [];
        state[category].accounts = [];
      }

      if (transactions) {
        // handle no transactions case
        if (transactions.length === 0) state[category].transactions ||= [];
        // handle UPSERT
        else state[category].transactions = [...state[category].transactions.filter((i) => !transactions.some((p) => p.id === i.id)), ...transactions.filter((t) => t.importFlag !== 'delete')];
        // if this is for accounts, insert all cross-category common required fields ; parse tags and importFileMappings to objects
      }

      if (simulatedTransactions) {
        // handle no transactions
        if (simulatedTransactions.length === 0) state[category].simulatedTransactions ||= [];
        // handle UPSERT
        else {
          state[category].simulatedTransactions = [
            ...state[category].simulatedTransactions.filter((i) => !simulatedTransactions.some((p) => p.id === i.id)),
            ...simulatedTransactions.filter((t) => t.importFlag !== 'delete'),
          ].map(getTransactionTransformationsCallback(category));
        }
      }

      if (accounts) {
        // handle no accounts
        if (accounts.length === 0) state[category].accounts ||= [];
        // handle UPSERT
        else state[category].accounts = [...state[category].accounts.filter((xa) => !accounts.some((na) => na.id === xa.id)), ...accounts.filter((t) => t.importFlag !== 'delete')];
      }

      if (settings) {
        state[category].settings = settings;
      }
    },
  },
  extraReducers: {},
});

export default dataSlice.reducer;

export const { updateStoreData } = dataSlice.actions;

// export all objects from the submodules
export * from './globalSelectors/overarching';
export * from './globalSelectors/deposits';
export * from './globalSelectors/stocks';
export * from './globalSelectors/realEstate';
export * from './globalSelectors/loans';
export * from './globalSelectors/pension';
export * from './globalSelectors/objectsOfValue';
export * from './globalSelectors/metals';
export * from './globalSelectors/unlistedShares';
export * from './globalSelectors/crypto';
export * from './globalSelectors';
